import { Component, Directive, HostListener, Input } from '@angular/core';

/**
 * Replaces <mdb-table-pagination>. Templates filter their rows with
 * `i + 1 >= pager.firstItemIndex && i < pager.lastItemIndex`, so this keeps those two
 * fields with MDB's semantics (1-based first index, 10 rows per page).
 */
@Component({
    selector: 'app-table-pagination',
    exportAs: 'tablePagination',
    template: `
    <div class="d-flex align-items-center justify-content-end cs-table-pagination">
      <span class="mr-3">{{ total ? firstItemIndex : 0 }} - {{ lastVisibleItemIndex }} of {{ total }}</span>
      <button type="button" class="btn btn-sm btn-light" [disabled]="activePageNumber <= 1" (click)="previousPage()">
        <i class="fas fa-chevron-left"></i>
      </button>
      <button type="button" class="btn btn-sm btn-light" [disabled]="lastItemIndex >= total" (click)="nextPage()">
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>`,
    standalone: false
})
export class TablePaginationComponent {
  /** rows being paged (MDB's [searchDataSource]) */
  @Input() searchDataSource: any[] = [];
  @Input() maxVisibleItems = 10;

  activePageNumber = 1;

  get total(): number { return this.searchDataSource ? this.searchDataSource.length : 0; }
  get firstItemIndex(): number { return (this.activePageNumber - 1) * this.maxVisibleItems + 1; }
  get lastItemIndex(): number { return this.activePageNumber * this.maxVisibleItems; }
  get lastVisibleItemIndex(): number { return Math.min(this.lastItemIndex, this.total); }

  previousPage() { if (this.activePageNumber > 1) { this.activePageNumber--; } }
  nextPage() { if (this.lastItemIndex < this.total) { this.activePageNumber++; } }
}

/**
 * Replaces MDB's [mdbTableSort]="rows" [sortBy]="key": clicking the header sorts the
 * array in place, toggling ascending/descending on each click. Dotted keys are nested.
 */
@Directive({
    selector: '[appTableSort]',
    standalone: false
})
export class TableSortDirective {
  @Input('appTableSort') rows: any[];
  @Input() sortBy: string;
  private ascending = false;

  @HostListener('click')
  sort() {
    if (!this.rows || !this.sortBy) { return; }
    const path = this.sortBy.toString().replace(/ /g, '').split('.');
    const get = (row: any) => path.reduce((v, k) => (v == null ? v : v[k]), row);
    this.ascending = !this.ascending;
    const dir = this.ascending ? 1 : -1;
    this.rows.sort((a, b) => {
      const x = get(a), y = get(b);
      return x < y ? -dir : x > y ? dir : 0;
    });
  }
}
